/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { usePageDesignerMode } from '@salesforce/storefront-next-runtime/design/react/core';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import type { ComponentType } from '@/components/region';
import { Component as RegionComponent } from '@/components/region/component';
import { RegionWrapper } from '@/components/region/region-wrapper';
import { cn } from '@/lib/utils';

const BACKGROUND_CLASS = {
    black: 'bg-ps-black',
    'primary-static': 'bg-ps-primary-static',
    'primary-interactive': 'bg-ps-primary-interactive',
    accent: 'bg-ps-accent',
    'gray-100': 'bg-ps-gray-100',
    white: 'bg-ps-white',
} as const;

const TEXT_CLASS = {
    white: 'text-ps-white',
    black: 'text-ps-black',
    'primary-static': 'text-ps-primary-static',
    accent: 'text-ps-accent',
} as const;

const announcementCarouselDefaults = {
    backgroundColor: 'black',
    textColor: 'white',
    autoPlay: true,
    autoPlayInterval: 5000,
} as const;

/**
 * Direction-aware crossfade + short slide (tw-animate-css). Going forward (next arrow or
 * auto-rotation) the new message enters from the right and the old one leaves to the left;
 * going back mirrors it. `motion-safe:` drops the motion for prefers-reduced-motion.
 *
 * tw-animate's `exit` keyframes only define `to`, so the leaving slide starts from its own computed
 * style: it must be visible (`opacity-100`) for the fade to run, and `fill-mode-forwards` keeps it
 * hidden after the animation. Without motion it falls back to the plain `opacity-0`.
 */
const SLIDE_ANIMATION = {
    next: {
        enter: 'motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-right-4',
        exit: 'motion-safe:animate-out motion-safe:fade-out motion-safe:slide-out-to-left-4 motion-safe:opacity-100 motion-safe:fill-mode-forwards',
    },
    previous: {
        enter: 'motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-left-4',
        exit: 'motion-safe:animate-out motion-safe:fade-out motion-safe:slide-out-to-right-4 motion-safe:opacity-100 motion-safe:fill-mode-forwards',
    },
} as const;
const SLIDE_ANIMATION_TIMING = 'motion-safe:duration-500 motion-safe:ease-out';

type Direction = keyof typeof SLIDE_ANIMATION;

/** Floor for the rotation interval: PD does not validate ranges, and faster rotation is unreadable. */
const MIN_AUTO_PLAY_INTERVAL = 2000;

function resolveToken<T extends Record<string, string>>(map: T, value: string | undefined, fallback: keyof T): string {
    return value && Object.hasOwn(map, value) ? map[value] : map[fallback];
}

@Component('announcementCarousel', {
    name: 'Announcement Carousel',
    description:
        'Pre-header shown below the main menu. Rotates Announcement Messages with prev/next arrows and optional auto play.',
    group: 'priceshoes_layout',
})
@RegionDefinition([
    {
        id: 'messages',
        name: 'Messages',
        description: 'Add Announcement Message components. Each message is shown one at a time.',
        maxComponents: 10,
        componentTypeInclusions: ['priceshoes_content.announcementMessage'],
    },
])
// oxlint-disable-next-line react/only-export-components -- oxlint flags the co-exported Page Designer metadata class; eslint-plugin-react-refresh does not
export class AnnouncementCarouselMetadata {
    @AttributeDefinition({
        id: 'backgroundColor',
        name: 'Background Color',
        description: 'Brand color token used as the pre-header background',
        type: 'enum',
        // Literal list: the cartridge generator reads decorators statically. Keep in sync with BACKGROUND_CLASS.
        values: ['black', 'primary-static', 'primary-interactive', 'accent', 'gray-100', 'white'],
        defaultValue: announcementCarouselDefaults.backgroundColor,
    })
    backgroundColor?: string;

    @AttributeDefinition({
        id: 'textColor',
        name: 'Text Color',
        description: 'Brand color token used for the message text and arrows',
        type: 'enum',
        // Literal list: the cartridge generator reads decorators statically. Keep in sync with TEXT_CLASS.
        values: ['white', 'black', 'primary-static', 'accent'],
        defaultValue: announcementCarouselDefaults.textColor,
    })
    textColor?: string;

    @AttributeDefinition({
        id: 'autoPlay',
        name: 'Auto Play',
        description: 'Rotate messages automatically. When off, shoppers move between messages with the arrows.',
        type: 'boolean',
        defaultValue: announcementCarouselDefaults.autoPlay,
    })
    autoPlay?: boolean;

    @AttributeDefinition({
        id: 'autoPlayInterval',
        name: 'Rotation Interval (ms)',
        description: 'Time each message stays visible before rotating to the next one. Minimum 2000ms.',
        type: 'integer',
        defaultValue: announcementCarouselDefaults.autoPlayInterval,
    })
    autoPlayInterval?: number;
}

interface AnnouncementCarouselProps {
    backgroundColor?: string;
    textColor?: string;
    autoPlay?: boolean;
    autoPlayInterval?: number;
    /** Plain message list (Storybook/test path). Ignored when the PD `messages` region has components. */
    messages?: string[];
    /** Component data containing regions from Page Designer */
    component?: ComponentType;
    className?: string;
}

/** Stable empty-array reference so the `messages` default never changes identity between renders. */
const NO_MESSAGES: string[] = [];

function usePrefersReducedMotion(): boolean {
    const [reduced, setReduced] = useState(false);
    useEffect(() => {
        setReduced(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    }, []);
    return reduced;
}

export default function AnnouncementCarousel({
    backgroundColor,
    textColor,
    autoPlay = announcementCarouselDefaults.autoPlay,
    autoPlayInterval = announcementCarouselDefaults.autoPlayInterval,
    messages = NO_MESSAGES,
    component,
    className,
}: AnnouncementCarouselProps): ReactElement | null {
    const { t } = useTranslation('common');
    const { isDesignMode } = usePageDesignerMode();

    const messagesRegion = useMemo(
        () => (Array.isArray(component?.regions) ? component.regions.find((r) => r.id === 'messages') : undefined),
        [component]
    );
    const messagesRegionMetadata = useMemo(
        () => component?.designMetadata?.regionDefinitions?.find((r) => r.id === 'messages'),
        [component]
    );

    const regionComponents = useMemo(() => {
        if (!Array.isArray(messagesRegion?.components)) return [];
        return messagesRegion.components.filter((comp) => comp.id && comp.typeId) as ComponentType[];
    }, [messagesRegion]);

    const slides = useMemo<{ key: string; content: ReactNode }[]>(
        () =>
            regionComponents.length > 0
                ? regionComponents.map((comp) => ({
                      key: comp.contentLinkUuid ?? comp.id,
                      content: <RegionComponent component={comp} regionId="messages" />,
                  }))
                : messages
                      .filter((message) => message.trim())
                      .map((message, index) => ({
                          key: `${index}`,
                          content: <p className="text-center">{message}</p>,
                      })),
        [regionComponents, messages]
    );

    const slideCount = slides.length;
    // `previous` stays null until the first change so the initial (SSR) message never animates.
    const [position, setPosition] = useState<{ index: number; previous: number | null; direction: Direction }>({
        index: 0,
        previous: null,
        direction: 'next',
    });
    const [isPaused, setIsPaused] = useState(false);
    const [hasInteracted, setHasInteracted] = useState(false);
    const prefersReducedMotion = usePrefersReducedMotion();
    const interval = Math.max(
        MIN_AUTO_PLAY_INTERVAL,
        autoPlayInterval || announcementCarouselDefaults.autoPlayInterval
    );
    const activeIndex = slideCount > 0 ? position.index % slideCount : 0;
    const animation = SLIDE_ANIMATION[position.direction];

    const step = useCallback(
        (direction: Direction) =>
            setPosition(({ index }) => ({
                index: (index + (direction === 'next' ? 1 : -1) + slideCount) % slideCount,
                previous: index,
                direction,
            })),
        [slideCount]
    );

    // Runs only after hydration; the server always renders the first message. Re-arming on every
    // index change restarts the countdown after an arrow click.
    useEffect(() => {
        if (!autoPlay || slideCount < 2 || isPaused || prefersReducedMotion) return;
        const timer = setTimeout(() => step('next'), interval);
        return () => clearTimeout(timer);
    }, [activeIndex, autoPlay, slideCount, isPaused, prefersReducedMotion, interval, step]);

    const goTo = useCallback(
        (direction: Direction) => {
            setHasInteracted(true);
            step(direction);
        },
        [step]
    );

    // Page Designer maps each authored message to its region through the RegionWrapper's context.
    // Rendering the slides inside it is what makes the messages selectable in the editor (instead of
    // greyed out) and keeps the region a drop target. On the storefront it adds no DOM.
    const withMessagesRegion = (children: ReactNode) =>
        messagesRegion ? (
            <RegionWrapper
                region={messagesRegion}
                designMetadata={{
                    id: 'messages',
                    componentTypeInclusions: messagesRegionMetadata?.componentTypeInclusions ?? [],
                    componentTypeExclusions: messagesRegionMetadata?.componentTypeExclusions ?? [],
                }}>
                {children}
            </RegionWrapper>
        ) : (
            children
        );

    const colorClass = cn(
        resolveToken(BACKGROUND_CLASS, backgroundColor, announcementCarouselDefaults.backgroundColor),
        resolveToken(TEXT_CLASS, textColor, announcementCarouselDefaults.textColor)
    );

    if (slideCount === 0) {
        // Authoring affordance only: on the live storefront an empty pre-header renders nothing (CA7).
        if (!isDesignMode) return null;
        return (
            <div
                data-slot="announcement-carousel-empty"
                className={cn('flex min-h-10 items-center justify-center px-4 py-2 text-sm', colorClass, className)}>
                {withMessagesRegion(<p>{t('announcementCarousel.emptyLabel')}</p>)}
            </div>
        );
    }

    const showArrows = slideCount > 1;

    return (
        // oxlint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- hover/focus handlers only pause auto-rotation; the arrows are the interactive controls
        <section
            data-slot="announcement-carousel"
            aria-roledescription="carousel"
            aria-label={t('announcementCarousel.label')}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onFocus={() => setIsPaused(true)}
            onBlur={() => setIsPaused(false)}
            className={cn(
                'flex min-h-10 items-center justify-center gap-4 px-4 py-2 text-sm leading-5',
                colorClass,
                className
            )}>
            {showArrows && (
                <button
                    type="button"
                    onClick={() => goTo('previous')}
                    aria-label={t('announcementCarousel.previous')}
                    className="flex size-6 shrink-0 cursor-pointer items-center justify-center hover:opacity-70 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current">
                    <ChevronLeft className="size-4" aria-hidden="true" />
                </button>
            )}
            {/* Sizing lives on this outer div: in design mode the region decorator inserts its own
                frame elements, so it cannot carry the grid itself. */}
            <div className="min-w-0 max-w-3xl flex-1 sm:flex-none">
                {withMessagesRegion(
                    // Every slide shares one grid cell, so the row is as tall as the longest message
                    // and rotating between one- and two-line messages never shifts the page (CA6).
                    <div
                        data-slot="announcement-slides"
                        aria-live={hasInteracted ? 'polite' : 'off'}
                        className="grid overflow-x-clip">
                        {slides.map((slide, index) => {
                            const isActive = index === activeIndex;
                            const hasMoved = position.previous !== null;
                            const isLeaving = hasMoved && !isActive && index === position.previous;
                            return (
                                <div
                                    key={slide.key}
                                    data-slot="announcement-slide"
                                    aria-hidden={isActive ? undefined : true}
                                    inert={isActive ? undefined : true}
                                    className={cn(
                                        '[grid-area:1/1] flex items-center justify-center',
                                        isActive ? 'opacity-100' : 'opacity-0',
                                        isActive && hasMoved && cn(animation.enter, SLIDE_ANIMATION_TIMING),
                                        isLeaving && cn(animation.exit, SLIDE_ANIMATION_TIMING)
                                    )}>
                                    {slide.content}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            {showArrows && (
                <button
                    type="button"
                    onClick={() => goTo('next')}
                    aria-label={t('announcementCarousel.next')}
                    className="flex size-6 shrink-0 cursor-pointer items-center justify-center hover:opacity-70 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current">
                    <ChevronRight className="size-4" aria-hidden="true" />
                </button>
            )}
        </section>
    );
}
